import type { Expense } from "../expenses/types";
import type { Invoice } from "../invoices/types";
import type { ExportDataSources, ExportRequest } from "./types";

// RFC4180-style escape: wrap in quotes and double any embedded quote. Any cell
// that contains a comma, quote, CR, or LF must be quoted.
function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str === "") return "";
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function centsToDecimal(cents: number): string {
  // Intentional: write the raw numeric value for spreadsheets. Currency is a
  // separate column, so don't format as "$1,234.56" here.
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${sign}${dollars}.${remainder.toString().padStart(2, "0")}`;
}

// YYYY-MM-DD string compare is lexicographic and therefore correct for
// inclusive range filtering.
function withinRange(
  isoDate: string,
  from: string | null | undefined,
  to: string | null | undefined,
): boolean {
  if (from && isoDate < from) return false;
  if (to && isoDate > to) return false;
  return true;
}

function taxPolicyLabelFor(
  expense: Expense,
  taxPolicies: ExportDataSources["taxPolicies"],
): string {
  if (expense.taxPolicyLabel) return expense.taxPolicyLabel;
  if (!expense.taxPolicyId) return "";
  const match = taxPolicies.find((p) => p.id === expense.taxPolicyId);
  return match?.category ?? "";
}

function categoryLabelFor(
  expense: Expense,
  categories: ExportDataSources["categories"],
): string {
  if (expense.categoryLabel) return expense.categoryLabel;
  if (!expense.categoryId) return "";
  const match = categories.find((c) => c.id === expense.categoryId);
  return match?.label ?? "";
}

function receiptIdsForExpense(
  expenseId: string,
  receipts: ExportDataSources["receipts"],
): string {
  return receipts
    .filter((r) => r.linkedExpenseId === expenseId)
    .map((r) => r.id)
    .join("|");
}

function receiptIdsForInvoice(
  invoiceId: string,
  receipts: ExportDataSources["receipts"],
): string {
  return receipts
    .filter((r) => r.linkedInvoiceId === invoiceId)
    .map((r) => r.id)
    .join("|");
}

export interface BuildCsvResult {
  content: string;
  rowCount: number;
}

// Pure CSV builder. Given the live data + a request, produce the file
// content and row count. No side effects, no Blob, no download — the caller
// (store implementation) is responsible for wrapping this and triggering the
// browser download.
export function buildExpensesCsv(
  sources: ExportDataSources,
  request: ExportRequest,
): BuildCsvResult {
  const { expenses, invoices, categories, taxPolicies, receipts } = sources;
  const categoryFilter = request.categoryIds?.length
    ? new Set(request.categoryIds)
    : null;
  const includeReceipts = Boolean(request.includeReceipts);
  const includeNotes = Boolean(request.includeNotes);

  // Column set. The base columns are always present; optional columns append
  // to the right. Keep the header exactly aligned with the row emission
  // below.
  const header = [
    "Type",
    "Date",
    "Counterparty",
    "Amount",
    "Currency",
    "Category",
    "Tax Policy",
    "Deductible",
    "Status",
    "Notes",
  ];
  if (includeReceipts) header.push("Receipt IDs");
  if (includeNotes) header.push("Business Purpose");

  const lines: string[] = [header.map(csvEscape).join(",")];

  // --- Expenses -------------------------------------------------------------
  for (const e of expenses) {
    if (!withinRange(e.occurredOn, request.dateFrom, request.dateTo)) continue;
    if (request.userId && e.submittedBy !== request.userId) continue;
    if (categoryFilter) {
      // An expense with no category is only included if the caller didn't
      // narrow to specific categories.
      if (!e.categoryId || !categoryFilter.has(e.categoryId)) continue;
    }

    const row = [
      "expense",
      e.occurredOn,
      e.vendor,
      centsToDecimal(e.amountCents),
      e.currency,
      categoryLabelFor(e, categories),
      taxPolicyLabelFor(e, taxPolicies),
      e.deductibleAmountCents !== null
        ? centsToDecimal(e.deductibleAmountCents)
        : "",
      e.status,
      "", // Notes column — expenses don't carry free-form notes on the top-level schema
    ];
    if (includeReceipts) row.push(receiptIdsForExpense(e.id, receipts));
    if (includeNotes) row.push(e.businessPurpose ?? "");
    lines.push(row.map(csvEscape).join(","));
  }

  // --- Invoices -------------------------------------------------------------
  // Invoices don't have a category, so we skip them entirely if the caller
  // narrowed to specific categories (intentional: category filters are an
  // expense concern).
  const skipInvoices = categoryFilter !== null;
  if (!skipInvoices) {
    for (const i of invoices) {
      if (!withinRange(i.issuedOn, request.dateFrom, request.dateTo)) continue;
      if (request.userId && i.submittedBy !== request.userId) continue;

      const row = [
        "invoice",
        i.issuedOn,
        i.counterparty,
        centsToDecimal(i.amountCents),
        i.currency,
        "", // Category — n/a for invoices
        "", // Tax Policy — n/a
        "", // Deductible — n/a
        i.status,
        i.notes ?? "",
      ];
      if (includeReceipts) row.push(receiptIdsForInvoice(i.id, receipts));
      if (includeNotes) row.push(""); // Invoices have no business purpose
      lines.push(row.map(csvEscape).join(","));
    }
  }

  return {
    content: lines.join("\n") + "\n",
    rowCount: lines.length - 1, // exclude header
  };
}

export function buildExportFilename(
  format: "csv" | "pdf" | "zip",
  createdAt: Date,
): string {
  const yyyy = createdAt.getFullYear();
  const mm = String(createdAt.getMonth() + 1).padStart(2, "0");
  const dd = String(createdAt.getDate()).padStart(2, "0");
  const hh = String(createdAt.getHours()).padStart(2, "0");
  const mi = String(createdAt.getMinutes()).padStart(2, "0");
  return `proofly_export_${yyyy}${mm}${dd}_${hh}${mi}.${format}`;
}

// QuickBooks Online import format — Date, Name, Amount, Account Code, Memo
export function buildQuickBooksCsv(
  sources: ExportDataSources,
  request: ExportRequest,
): BuildCsvResult {
  const { expenses, categories } = sources;
  const categoryFilter = request.categoryIds?.length
    ? new Set(request.categoryIds)
    : null;
  const header = ["Date", "Name", "Amount", "Account Code", "Memo"];
  const lines: string[] = [header.map(csvEscape).join(",")];
  for (const e of expenses) {
    if (!withinRange(e.occurredOn, request.dateFrom, request.dateTo)) continue;
    if (categoryFilter && (!e.categoryId || !categoryFilter.has(e.categoryId))) continue;
    lines.push(
      [
        e.occurredOn,
        e.vendor,
        centsToDecimal(e.amountCents),
        categoryLabelFor(e, categories),
        e.businessPurpose ?? "",
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return { content: lines.join("\n") + "\n", rowCount: lines.length - 1 };
}

// Xero import format — *Date, *Amount, *Description, Account Code, Tax Type
export function buildXeroCsv(
  sources: ExportDataSources,
  request: ExportRequest,
): BuildCsvResult {
  const { expenses, categories, taxPolicies } = sources;
  const categoryFilter = request.categoryIds?.length
    ? new Set(request.categoryIds)
    : null;
  const header = ["*Date", "*Amount", "*Description", "Account Code", "Tax Type"];
  const lines: string[] = [header.map(csvEscape).join(",")];
  for (const e of expenses) {
    if (!withinRange(e.occurredOn, request.dateFrom, request.dateTo)) continue;
    if (categoryFilter && (!e.categoryId || !categoryFilter.has(e.categoryId))) continue;
    lines.push(
      [
        e.occurredOn,
        centsToDecimal(e.amountCents),
        e.vendor,
        categoryLabelFor(e, categories),
        taxPolicyLabelFor(e, taxPolicies),
      ]
        .map(csvEscape)
        .join(","),
    );
  }
  return { content: lines.join("\n") + "\n", rowCount: lines.length - 1 };
}
